import { Translate } from "components/common/Translate/Translate";
import { X } from "lucide-react";
import { BulkResizerCropSettings } from "./CropSettings/BulkResizerCropSettings";
import { BulkResizeImage } from "@/store/features/bulkResize/types";
import { useBulkResizerCrop } from "./useBulkResizerCrop";
import CropAndResetButton from "./CropAndResetButton";
import { Modal } from "@/components/common/Modal/Modal";
import { Navigation } from "../Navigation/Navigation";

interface CropModalProps {
    image: BulkResizeImage;
    isOpen: boolean;
    onClose: () => void;
}

const BulkResizerSingleImageCropModal = ({ isOpen, image, onClose }: CropModalProps) => {
    const { imageRef, onImageLoad } = useBulkResizerCrop(image.id);
    return (
        <Modal isOpen={isOpen} close={onClose} closeOnOverlayClick={false}>
            <div
                className="bg-white flex flex-col max-h-[calc(100vh-120px)] md:min-h-[80vh] dark:bg-darkSurface-100 dark:border-2 dark:border-darkSurface-400 rounded-lg w-full max-w-[900px] mx-4 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-4 border-b dark:border-darkSurface-400">
                    <h3 className="text-lg lg:text-2xl font-semibold dark:text-white">
                        <Translate keyName="crop-image" />
                    </h3>
                    <Navigation imageId={image.id} operation="crop" />
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <X />
                    </button>
                </div>

                <div className="flex flex-grow flex-col md:flex-row max-h-full overflow-hidden">
                    <div className="md:w-2/3 border-r dark:border-darkSurface-100 p-4 flex items-center justify-center bg-gray-50 dark:bg-darkSurface-200">
                        <div className="flex items-center justify-center relative w-full h-full max-h-[40vh] md:max-h-full">
                            <img
                                src={image.preview.src}
                                key={image.preview.src}
                                onLoad={onImageLoad}
                                ref={imageRef}
                                className="max-w-full max-h-full object-contain"
                            />
                        </div>
                    </div>

                    <div className="md:w-1/3 flex flex-col justify-between overflow-hidden">
                        <div className="flex-grow overflow-y-auto">
                            <BulkResizerCropSettings imageId={image.id} />
                        </div>
                        <CropAndResetButton imageId={image.id} onClose={onClose} />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const BulkResizerSingleImageCropModalWrapper = (props: CropModalProps) => {
    if (!props.isOpen) return null;
    return <BulkResizerSingleImageCropModal {...props} />;
};

export default BulkResizerSingleImageCropModalWrapper;
